// src/components/quotes/QuoteKanban.jsx
//
// ⚠ NAMED QuoteKanban, NOT QuoteBoard. quoteBoard.js (the React-free rules)
// sits beside this file, and on a case-insensitive filesystem an import of
// "./QuoteBoard" resolves to it first (webpack tries .js before .jsx) — the
// build then fails with "does not contain a default export".
//
// The quote kanban: Draft · Sent · Won · Invoiced · Paid · Ordered · Autotask
// · Complete. The rules — which column, which steps, what a drop may do — live
// in quoteBoard.js (React-free, tested); this file only draws them.
//
// ⚠ DROPS: only the four fulfilment columns accept one, and a drop sets THAT
// ONE step, exactly as clicking its chip does. Draft, Sent, Won and Complete
// reject drops — see DROP_COLUMNS in quoteBoard.js for why each one does.
// Dragging a card OUT of a fulfilment column is not a gesture; untick the chip.
//
// ⚠ LAYOUT: eight columns will not fit a laptop, so the board scrolls
// horizontally with sticky column headers. The board root carries
// `minWidth: 0` — CLIENT_REGISTER_LAYOUT_BRIEF.md records the same class of
// bug on the client register, where a flex ancestor without min-width:0 meant
// the overflow-x:auto wrapper never engaged and the whole page went wide.
import React, { useState } from "react";
import QuoteCard from "./QuoteCard";
import { BOARD_COLUMNS, COLUMN_LABELS, groupByColumn, isDropColumn } from "./quoteBoard";

const DRAG_MIME = "text/x-quote-id";

export default function QuoteKanban({
  quotes, computeQuoteTotals, countAllLineItems, sumOptions, ensureMigrated,
  onOpen, onToggleStep, onDropStep, onDuplicate,
}) {
  const [over, setOver] = useState(null);
  const cols = groupByColumn(quotes);
  const byRecent = (a, b) => (b.updatedAt || 0) - (a.updatedAt || 0);

  return (
    <div data-testid="quote-board"
      style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", gap: 12, overflowX: "auto", overflowY: "hidden", padding: 14, alignItems: "stretch" }}>
      {BOARD_COLUMNS.map((col) => {
        const droppable = isDropColumn(col);
        const isOver = droppable && over === col;
        return (
          <div key={col}
            data-testid={`column-${col}`}
            data-droppable={droppable ? "true" : "false"}
            onDragOver={droppable ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (over !== col) setOver(col); } : undefined}
            onDragLeave={droppable ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(null); } : undefined}
            onDrop={droppable ? (e) => {
              e.preventDefault();
              setOver(null);
              const id = e.dataTransfer.getData(DRAG_MIME);
              if (id && onDropStep) onDropStep(id, col);
            } : undefined}
            style={{
              width: 300, minWidth: 300, flexShrink: 0, display: "flex", flexDirection: "column", minHeight: 0,
              background: isOver ? "var(--strat-d)" : "var(--s2)",
              border: `1px solid ${isOver ? "var(--strat)" : "var(--border)"}`, borderRadius: 8,
              overflowY: "auto", transition: "background 0.15s, border-color 0.15s",
            }}>
            <div style={{ position: "sticky", top: 0, zIndex: 1, padding: "8px 12px", background: "white", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{COLUMN_LABELS[col]}</span>
              <span data-testid={`count-${col}`} style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", background: "var(--s2)", padding: "1px 7px", borderRadius: 10 }}>{cols[col].length}</span>
            </div>
            <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
              {[...cols[col]].sort(byRecent).map((q) => (
                <QuoteCard key={q.id} quote={q}
                  computeQuoteTotals={computeQuoteTotals} countAllLineItems={countAllLineItems}
                  sumOptions={sumOptions} ensureMigrated={ensureMigrated}
                  onOpen={onOpen} onToggleStep={(step) => onToggleStep && onToggleStep(q.id, step)}
                  onDuplicate={onDuplicate}
                  /* only a won quote has steps to set, so only a won card drags */
                  draggable={q.status === "won"}
                  onDragStart={(e) => { e.dataTransfer.setData(DRAG_MIME, q.id); e.dataTransfer.effectAllowed = "move"; }} />
              ))}
              {cols[col].length === 0 && (
                <div style={{ fontSize: 10, color: "var(--text3)", fontStyle: "italic", textAlign: "center", padding: "18px 6px" }}>
                  {droppable ? "Drop a won quote here to tick this step" : "—"}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { DRAG_MIME };
